
import { Vue, Component, Watch } from 'vue-property-decorator';
import DocumentDropZone from '@/features/shipment/components/commercialInvoice/DocumentDropZone.vue';
import DocumentRow from '@/features/shipment/components/commercialInvoice/DocumentRow.vue';
import QuickActionsPanel, { QuickActionDoc } from '@/features/shipment/components/commercialInvoice/QuickActionsPanel.vue';
import SavedLibraryModal from '@/features/shipment/components/commercialInvoice/SavedLibraryModal.vue';
import {
  USMCA_VIRTUAL_DOC_ID,
  USMCA_QUICK_ACTION_ID,
} from '@/features/shipment/components/commercialInvoice/etd-constants';
import {
  readLibraryAttachments,
  writeLibraryAttachments,
  addLibraryAttachment,
  removeLibraryAttachment,
  removeByRealDocumentId,
  clearLibraryAttachments,
  LibraryAttachmentMap,
} from '@/features/shipment/helpers/libraryAttachmentStorage';
import {
  UploadDocument,
  MAX_DOCUMENTS,
  POLL_INTERVAL_MS,
} from '@/features/shipment/types/documentType';
import {
  SavedDocumentDto,
  getDocumentLibrary,
  saveToLibrary,
  attachFromLibrary,
  toggleUsmca,
  previewUsmca,
} from '@/features/shipment/services/document-library.service';
import { UserPreferences } from '@/features/settings/types';
import {
  patchPreferences,
} from '@/features/settings/services/preferences.services';
import { validateFile, mapFailedReason } from '@/features/shipment/helpers/documentValidation';
import {
  uploadDocument,
  patchDocumentType,
  pollDocumentStatuses,
  deleteDocument,
  fetchDocument,
  extractFileName,
} from '@/features/shipment/services/documents.service';
import { showErrorAlertDialog, showSuccessAlertDialog, scrollToElementById } from '@/features/common/helpers';
import { parseError } from '@/features/common/helpers/errorParser';
import { writePreviewLoadingHtml } from '@/features/common/helpers/previewLoadingHtml';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import { hideFloatingWidgets, showFloatingWidgets } from '@/features/feedback-tool/helpers';

@Component({
  components: {
    DocumentDropZone,
    DocumentRow,
    QuickActionsPanel,
    SavedLibraryModal,
  },
})
export default class SupportingDocuments extends Vue {
  private hideFloatingWidgets = hideFloatingWidgets;

  private showFloatingWidgets = showFloatingWidgets;

  // eslint-disable-next-line global-require
  private pinIconSrc = require('@/assets/images/electronic-trade-documents/pin-icon.svg');

  // eslint-disable-next-line global-require
  private pinIconActiveSrc = require('@/assets/images/electronic-trade-documents/pin-icon-active.svg');

  private isExpanded = true;

  private isPinned = false;

  private pendingDeleteId = '';

  private pollingTimer: ReturnType<typeof setInterval> | null = null;

  private maxDocuments = MAX_DOCUMENTS;

  private activeUploads = 0;

  private showTypeErrors = false;

  private showLibraryModal = false;

  private addedLibraryDocIds: Set<string> = new Set();

  // Maps library doc ID → real documentId in the shipment
  private libraryToDocId: Map<string, string> = new Map();

  private libraryDocs: SavedDocumentDto[] = [];

  private isLoadingLibrary = false;

  private loadingQuickActionIds: Set<string> = new Set();

  private static readonly USMCA_COUNTRIES = ['CA', 'US', 'MX'];

  get isInProgressData(): number {
    return this.$store.getters.getIsInProgressData;
  }

  get isUsmcaAdded(): boolean {
    // eslint-disable-next-line no-unused-expressions
    this.isInProgressData; // re-evaluate when patchInProgress fires
    const inProgress = this.$store.getters.getInProgress;
    return !!inProgress?.commercialInvoice?.includeUsmca;
  }

  get fromCountry(): string {
    return this.$store.getters.getShipFromInProgress?.countryCode || '';
  }

  get toCountry(): string {
    return this.$store.getters.getShipToInProgress?.countryCode || '';
  }

  get isUsmcaEligible(): boolean {
    return (
      SupportingDocuments.USMCA_COUNTRIES.includes(this.fromCountry)
      && SupportingDocuments.USMCA_COUNTRIES.includes(this.toCountry)
    );
  }

  get quickActionItems(): QuickActionDoc[] {
    const items: QuickActionDoc[] = [];

    if (this.isUsmcaEligible) {
      items.push({
        id: USMCA_QUICK_ACTION_ID,
        name: this.$t('documents.usmcaCertificate') as string || 'USMCA Certificate',
        documentType: 'OneTimeUSMCA',
        isAutoGenerated: true,
        isUsmca: true,
      });
    }

    for (const doc of this.libraryDocs) {
      items.push({
        id: doc.id,
        name: doc.originalFileName || doc.name,
        documentType: doc.documentType,
        isAutoGenerated: false,
        isUsmca: false,
        libraryDoc: doc,
      });
    }

    return items;
  }

  get addedQuickActionIds(): Set<string> {
    const ids = new Set(this.addedLibraryDocIds);
    if (this.isUsmcaAdded) {
      ids.add(USMCA_QUICK_ACTION_ID);
    }
    return ids;
  }

  private async fetchLibraryDocs(): Promise<void> {
    this.isLoadingLibrary = true;
    try {
      this.libraryDocs = await getDocumentLibrary();
    } catch {
      this.libraryDocs = [];
    } finally {
      this.isLoadingLibrary = false;
    }
    this.rehydrateLibraryAttachments();
  }

  /**
   * Reconstruct the "added" Quick Action state from localStorage.
   * Uses server-generated documentIds and numeric library IDs (reliable).
   * Prunes any mappings where the library doc or shipment doc no longer exists.
   */
  private rehydrateLibraryAttachments(): void {
    const persisted = readLibraryAttachments();
    const persistedKeys = Object.keys(persisted);
    if (!persistedKeys.length) {
      if (this.addedLibraryDocIds.size) this.addedLibraryDocIds = new Set();
      if (this.libraryToDocId.size) this.libraryToDocId = new Map();
      return;
    }

    const validDocIds = new Set(this.documents.map((d) => d.documentId));
    const validLibIds = new Set(this.libraryDocs.map((d) => String(d.id)));

    const added = new Set<string>();
    const mapping = new Map<string, string>();
    const cleaned: LibraryAttachmentMap = {};

    for (const libId of persistedKeys) {
      const realId = persisted[libId];
      // Only keep mapping if BOTH sides still exist.
      // If libraryDocs hasn't loaded yet, accept based on docs alone (library check skipped).
      const libExists = this.libraryDocs.length === 0 || validLibIds.has(libId);
      if (libExists && validDocIds.has(realId)) {
        added.add(libId);
        mapping.set(libId, realId);
        cleaned[libId] = realId;
      }
    }

    this.addedLibraryDocIds = added;
    this.libraryToDocId = mapping;

    // Prune stale entries from storage (only when we can trust libraryDocs).
    if (
      this.libraryDocs.length > 0
      && Object.keys(cleaned).length !== persistedKeys.length
    ) {
      writeLibraryAttachments(cleaned);
    }
  }

  get isUploading(): boolean {
    return this.activeUploads > 0;
  }

  get documents(): UploadDocument[] {
    return this.$store.getters.getUploadDocuments as UploadDocument[];
  }

  get displayDocuments(): UploadDocument[] {
    if (!this.isUsmcaAdded) return this.documents;
    const usmcaRow = {
      documentId: USMCA_VIRTUAL_DOC_ID,
      documentType: 'OneTimeUSMCA',
      originalFileName: `${this.$t('documents.usmcaCertificate') as string || 'USMCA Certificate'}.pdf`,
      mimeType: 'application/pdf',
      status: 'Clean',
    } as UploadDocument;
    return [usmcaRow, ...this.documents];
  }

  get documentCount(): number {
    return this.documents.length + (this.isUsmcaAdded ? 1 : 0);
  }

  get isMaxDocumentsReached(): boolean {
    return this.documentCount >= MAX_DOCUMENTS;
  }

  get remainingDocumentSlots(): number {
    return Math.max(0, MAX_DOCUMENTS - this.documentCount);
  }

  get hasProcessingDocs(): boolean {
    return this.$store.getters.hasProcessingDocuments;
  }

  get hasDocumentsWithoutType(): boolean {
    return this.$store.getters.hasDocumentsWithoutType;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get isUserPreferences(): number {
    return this.$store.getters.getIsUserPreferences;
  }

  @Watch('documents', { immediate: true })
  private onDocumentsChanged(docs: UploadDocument[]): void {
    this.rehydrateLibraryAttachments();
    if (!docs || !docs.length) return;
    const hasProcessing = docs.some(
      (doc) => doc.status === 'Processing' && !doc.documentId.startsWith('temp-'),
    );
    if (hasProcessing) {
      this.startBatchPolling();
    }
  }

  @Watch('isUserPreferences', { immediate: true })
  private onUserPreferencesChanged(): void {
    this.loadCollapsePreference();
  }

  @Watch('showLibraryModal')
  private onSavedLibraryModalVisibility(open: boolean): void {
    if (open) {
      this.hideFloatingWidgets();
      return;
    }
    this.showFloatingWidgets();
  }

  mounted(): void {
    this.loadCollapsePreference();
    this.fetchLibraryDocs();
    eventBus.$on(eventNames.VALIDATE_DOCUMENT_TYPES, this.onValidateDocumentTypes);
    eventBus.$on(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.onShipmentCleared);
  }

  beforeDestroy(): void {
    this.showFloatingWidgets();
    this.clearAllPolling();
    eventBus.$off(eventNames.VALIDATE_DOCUMENT_TYPES, this.onValidateDocumentTypes);
    eventBus.$off(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.onShipmentCleared);
  }

  get isShipmentCompleted(): boolean {
    return this.$store.getters.getShowSuccessShip;
  }

  @Watch('isShipmentCompleted')
  // eslint-disable-next-line class-methods-use-this
  private onShipmentCompletedChanged(completed: boolean): void {
    if (completed) clearLibraryAttachments();
  }

  private onShipmentCleared(): void {
    clearLibraryAttachments();
    this.addedLibraryDocIds = new Set();
    this.libraryToDocId = new Map();
  }

  // --- Quick Actions / Library ---

  private setQuickActionLoading(id: string, loading: boolean): void {
    const next = new Set(this.loadingQuickActionIds);
    if (loading) {
      next.add(id);
    } else {
      next.delete(id);
    }
    this.loadingQuickActionIds = next;
  }

  private async withQuickActionLoading(id: string, fn: () => Promise<void>): Promise<void> {
    this.setQuickActionLoading(id, true);
    try {
      await fn();
    } finally {
      this.setQuickActionLoading(id, false);
    }
  }

  private async onQuickActionAdd(item: QuickActionDoc): Promise<void> {
    await this.withQuickActionLoading(String(item.id), async () => {
      if (item.isUsmca) {
        await this.onAddUsmca();
      } else if (item.libraryDoc) {
        await this.onAddFromQuickActions(item.libraryDoc);
      }
    });
  }

  private async onQuickActionRemove(item: QuickActionDoc): Promise<void> {
    await this.withQuickActionLoading(String(item.id), async () => {
      if (item.isUsmca) {
        await this.onRemoveUsmca();
      } else if (item.libraryDoc) {
        await this.onRemoveFromQuickActions(item.libraryDoc);
      }
    });
  }

  private async onAddUsmca(): Promise<void> {
    if (this.isMaxDocumentsReached) {
      showErrorAlertDialog(
        this.$t('documents.maxDocumentsReached') as string,
      );
      return;
    }
    const result = await toggleUsmca(true);
    if (!result) {
      showErrorAlertDialog(
        this.$t('documents.errorMessages.usmcaToggleFailed') as string
          || 'Failed to enable USMCA certificate',
      );
      return;
    }
    const inProgress = this.$store.getters.getInProgress;
    this.$store.dispatch('saveInProgressData', {
      key: 'commercialInvoice',
      value: {
        ...(inProgress?.commercialInvoice || {}),
        includeUsmca: true,
      },
    });
  }

  private async onRemoveUsmca(): Promise<void> {
    const result = await toggleUsmca(false);
    if (!result) {
      showErrorAlertDialog(
        this.$t('documents.errorMessages.usmcaToggleFailed') as string
          || 'Failed to disable USMCA certificate',
      );
      return;
    }
    const inProgress = this.$store.getters.getInProgress;
    this.$store.dispatch('saveInProgressData', {
      key: 'commercialInvoice',
      value: {
        ...(inProgress?.commercialInvoice || {}),
        includeUsmca: false,
      },
    });
  }

  private async onViewUsmca(): Promise<void> {
    const newWindow = window.open('', '_blank');
    if (!newWindow) {
      showErrorAlertDialog('Popup blocked. Please allow popups for this site.');
      return;
    }
    try {
      writePreviewLoadingHtml(newWindow, {
        titleText: this.$t('documents.usmcaPreviewLoading') as string,
        hintText: this.$t('documents.previewLoadingHint') as string,
        tabTitle: this.$t('documents.usmcaCertificate') as string,
      });
      const blob = await previewUsmca();
      if (!blob) {
        newWindow.close();
        showErrorAlertDialog(
          this.$t('documents.errorMessages.usmcaPreviewFailed') as string
            || 'Failed to load USMCA preview',
        );
        return;
      }
      const fileURL = URL.createObjectURL(blob);
      newWindow.location.href = fileURL;
      newWindow.document.title = 'USMCA Certificate';
      setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
    } catch {
      newWindow.close();
      showErrorAlertDialog(
        this.$t('documents.errorMessages.usmcaPreviewFailed') as string
          || 'Failed to load USMCA preview',
      );
    }
  }

  private async onDownloadUsmca(): Promise<void> {
    const blob = await previewUsmca();
    if (!blob) {
      showErrorAlertDialog(
        this.$t('documents.errorMessages.usmcaPreviewFailed') as string
          || 'Failed to load USMCA preview',
      );
      return;
    }
    const fileURL = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = fileURL;
    link.setAttribute('download', 'usmca-certificate.pdf');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(fileURL), 100);
  }

  private async onAddFromQuickActions(doc: SavedDocumentDto): Promise<void> {
    if (this.isMaxDocumentsReached) return;

    const docIdStr = String(doc.id);

    // Call BE to copy library doc into current shipment
    const result = await attachFromLibrary(doc.id);
    if (!result) {
      showErrorAlertDialog(
        this.$t('documents.errorMessages.attachFailed') as string
          || 'Failed to attach document from library',
      );
      return;
    }

    // Track mapping: library ID → real documentId (in-memory + localStorage)
    this.libraryToDocId.set(docIdStr, result.documentId);
    this.libraryToDocId = new Map(this.libraryToDocId);
    addLibraryAttachment(docIdStr, result.documentId);

    const next = new Set(this.addedLibraryDocIds);
    next.add(docIdStr);
    this.addedLibraryDocIds = next;

    // Add the real UploadDocument returned by BE to local store
    this.$store.commit('addUploadDocument', result);
  }

  private async onRemoveFromQuickActions(doc: SavedDocumentDto): Promise<void> {
    const docIdStr = String(doc.id);
    const realDocId = this.libraryToDocId.get(docIdStr);

    if (realDocId) {
      // Delete the real document via existing endpoint
      const success = await deleteDocument(realDocId);
      if (success) {
        this.$store.commit('removeDocument', realDocId);
        this.syncDocumentsToStore();
      }
      this.libraryToDocId.delete(docIdStr);
    }
    this.libraryToDocId = new Map(this.libraryToDocId);
    removeLibraryAttachment(docIdStr);

    const next = new Set(this.addedLibraryDocIds);
    next.delete(docIdStr);
    this.addedLibraryDocIds = next;
  }

  private async onAddFromLibrary(docs: SavedDocumentDto[]): Promise<void> {
    // Sequential to respect max documents limit
    // eslint-disable-next-line no-await-in-loop
    for (const doc of docs) {
      if (!this.isMaxDocumentsReached) {
        // eslint-disable-next-line no-await-in-loop
        await this.onAddFromQuickActions(doc);
      }
    }
  }

  private onLibraryDocUpdated(updated: SavedDocumentDto): void {
    this.libraryDocs = this.libraryDocs.map(
      (doc) => (doc.id === updated.id ? updated : doc),
    );
  }

  private onLibraryDocDeleted(deletedId: number): void {
    // Note: `attach-from-library` copies the S3 file into the shipment's WIP
    // folder, so the attached document is a standalone copy. We intentionally
    // do NOT cascade-delete the attached copy when the library entry is
    // removed — the user can still remove it via the document row's overflow
    // menu. Here we only clear the tracking state tied to the library entry.
    const idStr = String(deletedId);
    this.libraryDocs = this.libraryDocs.filter((doc) => doc.id !== deletedId);

    if (this.addedLibraryDocIds.has(idStr)) {
      const next = new Set(this.addedLibraryDocIds);
      next.delete(idStr);
      this.addedLibraryDocIds = next;
    }
    this.libraryToDocId.delete(idStr);
    this.libraryToDocId = new Map(this.libraryToDocId);
    removeLibraryAttachment(idStr);
  }

  get savedToLibraryDocIds(): Set<string> {
    return new Set(this.libraryToDocId.values());
  }

  private async onSaveToLibrary(documentId: string): Promise<void> {
    if (this.savedToLibraryDocIds.has(documentId)) {
      showErrorAlertDialog(
        this.$t('documents.alreadySavedToLibrary') as string
          || 'This document is already saved to your library',
      );
      return;
    }

    const doc = this.documents.find((d) => d.documentId === documentId);
    if (!doc) return;

    const result = await saveToLibrary({
      name: doc.originalFileName,
      documentType: doc.documentType || 'NotSet',
      documentId,
    });

    if (result) {
      showSuccessAlertDialog(this.$t('documents.savedToLibrary') as string || 'Document saved to library');

      // Mark the new library entry as "already added" so Quick Actions
      // shows the "added" state instead of a + button.
      const libIdStr = String(result.id);
      this.libraryToDocId.set(libIdStr, documentId);
      this.libraryToDocId = new Map(this.libraryToDocId);
      addLibraryAttachment(libIdStr, documentId);
      const next = new Set(this.addedLibraryDocIds);
      next.add(libIdStr);
      this.addedLibraryDocIds = next;
    } else {
      showErrorAlertDialog(this.$t('documents.saveToLibraryFailed') as string || 'Failed to save document to library');
    }

    // Always refresh library list after save attempt
    this.fetchLibraryDocs();
  }

  // --- Existing functionality (unchanged) ---

  private onValidateDocumentTypes(): void {
    if (this.hasDocumentsWithoutType) {
      this.showTypeErrors = true;
      this.isExpanded = true;
      this.$nextTick(() => {
        scrollToElementById('supporting-documents-section');
      });
    }
  }

  private loadCollapsePreference(): void {
    const autoCollapse = this.userPreferences
      ?.websiteSettings?.autoCollapseSupportingDocuments;

    this.isPinned = autoCollapse === false;
    this.isExpanded = autoCollapse !== true;
  }

  private toggleExpanded(): void {
    if (!this.isExpanded) {
      this.isExpanded = true;
      return;
    }

    if (this.hasProcessingDocs) {
      showErrorAlertDialog(
        this.$t('documents.stillProcessing') as string,
      );
      return;
    }

    if (this.hasDocumentsWithoutType) {
      this.showTypeErrors = true;
      showErrorAlertDialog(
        this.$t('documents.errorMessages.documentTypeRequired') as string,
      );
      return;
    }

    this.isExpanded = false;
  }

  private async togglePin(): Promise<void> {
    const newPinnedState = !this.isPinned;
    this.isPinned = newPinnedState;

    const autoCollapse = !newPinnedState;

    try {
      this.$store.commit('patchUserPreferences', {
        key: 'websiteSettings/autoCollapseSupportingDocuments',
        value: autoCollapse,
      });

      if (this.userPreferences?.id) {
        await patchPreferences(this.userPreferences.id, [{
          path: 'websiteSettings/autoCollapseSupportingDocuments',
          op: 'replace',
          value: autoCollapse,
        }]);
      }
    } catch (error) {
      this.isPinned = !newPinnedState;
      this.$store.commit('patchUserPreferences', {
        key: 'websiteSettings/autoCollapseSupportingDocuments',
        value: !autoCollapse,
      });
      showErrorAlertDialog(
        'Failed to update pin preference. Please try again.',
      );
    }
  }

  private async onFilesSelected(files: File[]): Promise<void> {
    const shownErrorKeys = new Set<string>();
    files.forEach((file) => {
      const validation = validateFile(file, this.documentCount);
      if (!validation.valid) {
        if (!shownErrorKeys.has(validation.errorKey)) {
          shownErrorKeys.add(validation.errorKey);
          showErrorAlertDialog(this.$t(validation.errorKey) as string);
        }
        return;
      }
      this.processUpload(file);
    });
  }

  private async processUpload(file: File): Promise<void> {
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const tempDoc: UploadDocument = {
      documentId: tempId,
      originalFileName: file.name,
      mimeType: file.type,
      documentType: '',
      status: 'Processing',
    };

    this.$store.commit('addUploadDocument', tempDoc);
    this.showTypeErrors = false;

    this.activeUploads += 1;
    const result = await uploadDocument(file);
    this.activeUploads -= 1;

    if (!result.successful || !result.documentId) {
      this.$store.commit('removeDocument', tempId);
      showErrorAlertDialog(
        result.errorMessage
          || (this.$t('documents.errorMessages.uploadFailed') as string),
      );
      return;
    }

    const realDoc: UploadDocument = {
      documentId: result.documentId,
      originalFileName: file.name,
      mimeType: file.type,
      documentType: '',
      status: 'Processing',
    };
    this.$store.commit('replaceDocument', {
      oldDocumentId: tempId,
      newDocument: realDoc,
    });

    this.startBatchPolling();
  }

  private startBatchPolling(): void {
    if (this.pollingTimer) return;

    this.pollingTimer = setInterval(async () => {
      const processingIds = this.documents
        .filter((doc) => doc.status === 'Processing' && !doc.documentId.startsWith('temp-'))
        .map((doc) => doc.documentId);

      if (!processingIds.length) {
        this.stopBatchPolling();
        return;
      }

      const results = await pollDocumentStatuses(processingIds);
      if (!results) return;

      let changed = false;
      results.forEach((statusResult) => {
        if (statusResult.status === 'Clean') {
          this.$store.commit('updateDocumentStatus', {
            documentId: statusResult.documentId,
            status: 'Clean',
          });
          changed = true;
        } else if (statusResult.status === 'Failed') {
          const reasonKey = mapFailedReason(statusResult.failedReason || '');
          showErrorAlertDialog(this.$t(reasonKey) as string);

          this.$store.commit('removeDocument', statusResult.documentId);
          changed = true;
        }
      });

      if (changed) {
        this.syncDocumentsToStore();
      }
    }, POLL_INTERVAL_MS);
  }

  private stopBatchPolling(): void {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
    }
  }

  private clearAllPolling(): void {
    this.stopBatchPolling();
  }

  private async onTypeChanged(
    { documentId, documentType }: { documentId: string; documentType: string },
  ): Promise<void> {
    const previousType = this.documents.find(
      (doc) => doc.documentId === documentId,
    )?.documentType || '';

    this.$store.commit('updateDocumentType', { documentId, documentType });

    if (this.showTypeErrors && !this.hasDocumentsWithoutType) {
      this.showTypeErrors = false;
    }

    const result = await patchDocumentType(documentId, documentType);
    if (!result.successful) {
      this.$store.commit('updateDocumentType', {
        documentId,
        documentType: previousType,
      });
      showErrorAlertDialog(
        result.errorMessage
          || (this.$t('documents.errorMessages.typeChangeFailed') as string),
      );
    } else {
      this.syncDocumentsToStore();
    }
  }

  private async onView(documentId: string): Promise<void> {
    if (documentId === USMCA_VIRTUAL_DOC_ID) {
      await this.onViewUsmca();
      return;
    }

    const newWindow = window.open('', '_blank');

    if (!newWindow) {
      showErrorAlertDialog('Popup blocked. Please allow popups for this site.');
      return;
    }

    try {
      const doc = this.documents.find((d) => d.documentId === documentId);
      writePreviewLoadingHtml(newWindow, {
        titleText: this.$t('documents.viewLoading') as string,
        hintText: this.$t('documents.previewLoadingHint') as string,
        tabTitle: doc?.originalFileName || (this.$t('documents.viewLoading') as string),
      });

      const response = await fetchDocument(documentId);

      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const fileURL = URL.createObjectURL(blob);

      newWindow.location.href = fileURL;
      newWindow.document.title = doc?.originalFileName || 'Document';

      setTimeout(() => URL.revokeObjectURL(fileURL), 10000);
    } catch (error) {
      newWindow.close();

      const errorResponse = parseError(error);
      const errorMessage = errorResponse.errorMessage
      || (this.$t('documents.errorMessages.viewFailed') as string);

      showErrorAlertDialog(errorMessage);
    }
  }

  private async onDownload(documentId: string): Promise<void> {
    if (documentId === USMCA_VIRTUAL_DOC_ID) {
      await this.onDownloadUsmca();
      return;
    }

    try {
      const response = await fetchDocument(documentId);

      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const fileURL = URL.createObjectURL(blob);
      const doc = this.documents.find((d) => d.documentId === documentId);
      const fileName = doc?.originalFileName || extractFileName(response.headers);

      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', fileName);
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => URL.revokeObjectURL(fileURL), 100);
    } catch (error) {
      const errorResponse = parseError(error);
      const errorMessage = errorResponse.errorMessage
        || (this.$t('documents.errorMessages.downloadFailed') as string);

      showErrorAlertDialog(errorMessage);
    }
  }

  private onDeleteRequest(documentId: string): void {
    if (documentId === USMCA_VIRTUAL_DOC_ID) {
      this.withQuickActionLoading(USMCA_QUICK_ACTION_ID, () => this.onRemoveUsmca());
      return;
    }

    this.pendingDeleteId = documentId;

    eventBus.$once(eventNames.CONFIRMATION_DIALOG, async () => {
      const deletedId = this.pendingDeleteId;
      const success = await deleteDocument(deletedId);
      if (success) {
        this.$store.commit('removeDocument', deletedId);

        // If this was a library-attached doc, clear its "added" state
        let matchedLibId: string | null = null;
        this.libraryToDocId.forEach((realId, libId) => {
          if (realId === deletedId) matchedLibId = libId;
        });
        if (matchedLibId) {
          const next = new Set(this.addedLibraryDocIds);
          next.delete(matchedLibId);
          this.addedLibraryDocIds = next;
          this.libraryToDocId.delete(matchedLibId);
          this.libraryToDocId = new Map(this.libraryToDocId);
        }
        removeByRealDocumentId(deletedId);

        this.syncDocumentsToStore();
        showSuccessAlertDialog(
          this.$t('documents.deleteSuccess') as string,
        );
      } else {
        showErrorAlertDialog(
          this.$t('documents.errorMessages.uploadFailed') as string,
        );
      }
      this.pendingDeleteId = '';
    });

    this.$store.commit('toggleConfirmDialog', {
      show: true,
      content: this.$t('documents.deleteConfirm') as string,
      title: this.$t('alertDialog.areYouSure') as string,
      btnText: this.$t('documents.actions.delete') as string,
    });
  }

  private syncDocumentsToStore(): void {
    this.$store.dispatch('syncInProgressData');
  }
}
